import { useState, useCallback, useRef, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { sendChatMessage, fetchSessionDetail, listChatSessions } from "@/services/chatService";
import ChatHistoryMenu from "@/components/ChatHistoryMenu";
import type { SessionSummary, SessionDetailResponse } from "@/types/authService";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Send,
  Bot,
  User,
  X,
  Plus,
  Pencil,
  Check,
  XCircle,
  BarChart3,
  Shield,
  Zap,
  ChevronDown,
  Loader2,
  MessageSquare,
  Maximize2,
  Minimize2,
  RotateCcw
} from "lucide-react";
import type { ChartContextSource } from "@/analytics/charts/chart.context";
import type { ChartInfo } from "@/types/authService";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  turnInputTokens?: number;
  turnOutputTokens?: number;
  cached?: boolean;
}

interface TabUsage {
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
}

interface ChatTab {
  id: string;
  name: string;
  messages: Message[];
  chartContext: string | null;
  // null until the backend assigns one on the first turn's response
  sessionId: string | null;
  usage: TabUsage;
  // Monotonic recency stamp; the lowest is evicted first when over MAX_OPEN_TABS.
  lastActive: number;
}

const MAX_OPEN_TABS = 3;

const withCap = (tabs: ChatTab[]): ChatTab[] => {
  if (tabs.length <= MAX_OPEN_TABS) return tabs;
  const evict = new Set(
    [...tabs]
      .sort((a, b) => a.lastActive - b.lastActive)
      .slice(0, tabs.length - MAX_OPEN_TABS)
      .map((t) => t.id),
  );
  return tabs.filter((t) => !evict.has(t.id));
};

const emptyUsage = (): TabUsage => ({
  inputTokens: 0,
  outputTokens: 0,
  costUsd: 0,
});

const isDefaultTabName = (name: string): boolean =>
  name === "New chat" || /^Chat \d+$/.test(name);

// Reconstruct a tab's transcript from a persisted session detail.
const messagesFromDetail = (detail: SessionDetailResponse): Message[] =>
  detail.messages.map((m) => ({
    id: uuidv4(),
    role: m.role === "assistant" ? "assistant" as const : "user" as const,
    content: m.content,
    turnInputTokens: m.turn_input_tokens ?? undefined,
    turnOutputTokens: m.turn_output_tokens ?? undefined,
  }));

// Collapse a session's agent + router usage rollup into a tab's running totals.
const usageFromDetail = (detail: SessionDetailResponse): TabUsage => ({
  inputTokens: detail.usage.agent.input_tokens + detail.usage.router.input_tokens,
  outputTokens: detail.usage.agent.output_tokens + detail.usage.router.output_tokens,
  costUsd: detail.usage.total_cost_usd,
});

const costFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
});

interface TransformedChartData {
  monthlyUtilisationCombo?: { labels: string[]; chargeableHours: number[]; non_chargeableHours: number[]; utilisationPct: number[] };
  averageCor?: { labels: string[]; datasets: { label: string; data: (number | null)[] }[] };
  nonChargeableHoursByActivity?: { labels: string[]; datasets: { label: string; data: number[] }[] };
  utilisationByLocation?: { labels: string[]; datasets: { label: string; data: (number | null)[] }[] };
  nonChargeableHoursByActivityBreakdown?: { labels: string[]; datasets: { label: string; data: number[] }[] };
  nonChargeableGbpByService?: { labels: string[]; datasets: { label: string; data: number[] }[] };
  utilisationByGrade?: { labels: string[]; datasets: { label: string; data: (number | null)[] }[]; averageUtilPct: number | null };
  burnoutRisk?: { labels: string[]; datasets: { label: string; data: (number | null)[] }[] };
}

interface ChatInterfaceProps {
  onClose?: () => void;
  chartItems?: ChartContextSource[];
  isMaximized?: boolean;
  onToggleMaximize?: () => void;
  onResetSize?: () => void;
}

const SUGGESTED_QUESTIONS = [
  "What is the average utilisation rate across all teams?",
  "Which team has the highest utilisation this month?",
  "Show me utilisation trends for the past 6 months",
  "Compare utilisation between departments",
  "What factors are affecting low utilisation rates?",
];

const markdownComponents = {
  table: ({ node, ...props }: any) => (
    <div className="my-4 overflow-x-auto rounded-md border bg-card" style={{ maxWidth: 'calc(100%)' }}>
      <table className="text-sm min-w-max" {...props} />
    </div>
  ),
  thead: ({ node, ...props }: any) => (
    <thead className="bg-muted/50 [&_tr]:border-b" {...props} />
  ),
  tbody: ({ node, ...props }: any) => (
    <tbody className="[&_tr:last-child]:border-0" {...props} />
  ),
  tr: ({ node, ...props }: any) => (
    <tr className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted" {...props} />
  ),
  th: ({ node, ...props }: any) => (
    <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 whitespace-nowrap" {...props} />
  ),
  td: ({ node, ...props }: any) => (
    <td className="p-4 align-middle [&:has([role=checkbox])]:pr-0 whitespace-nowrap" {...props} />
  ),
};

const ChatInterface = ({ onClose, chartItems = [], isMaximized, onToggleMaximize, onResetSize }: ChatInterfaceProps) => {
  const [tabs, setTabs] = useState<ChatTab[]>(() => {
    const initialId = uuidv4();
    return [
      {
        id: initialId,
        name: "New chat",
        messages: [
          {
            id: uuidv4(),
            role: "assistant",
            content: "Hello! I'm Prism Ask. Ask me anything about the dataset, or select a suggested question below.",
          },
        ],
        chartContext: null,
        sessionId: null,
        usage: emptyUsage(),
        lastActive: 0,
      },
    ];
  });
  const [activeTabId, setActiveTabId] = useState(() => tabs[0].id);
  const [input, setInput] = useState("");
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // True while we hydrate the user's recent sessions on first mount.
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Hands out the next recency stamp; bumped whenever a tab is activated/opened.
  const activityCounter = useRef(0);

  const activeTab = tabs.find((t) => t.id === activeTabId) || tabs[0];

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [activeTab.messages, isLoading]);

  const MAX_COMPOSER_HEIGHT = 160;
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, MAX_COMPOSER_HEIGHT)}px`;
  }, [input]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { sessions } = await listChatSessions(0, MAX_OPEN_TABS);
        if (cancelled || sessions.length === 0) return;

        const ordered = [...sessions].reverse();
        const details = await Promise.all(
          ordered.map((s) => fetchSessionDetail(s.session_id)),
        );
        if (cancelled) return;

        const hydrated: ChatTab[] = ordered.map((s, i) => {
          const detail = details[i];
          return {
            id: uuidv4(),
            name: detail.title || s.title || s.last_message_preview || "Untitled chat",
            messages: messagesFromDetail(detail),
            chartContext: null,
            sessionId: detail.session_id,
            usage: usageFromDetail(detail),
            lastActive: i + 1,
          };
        });
        activityCounter.current = hydrated.length;
        setTabs(hydrated);
        setActiveTabId(hydrated[hydrated.length - 1].id);
      } catch (err) {
        console.warn("Failed to restore recent chat sessions:", err);
      } finally {
        if (!cancelled) setIsBootstrapping(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Activate a tab and bump its recency so it survives eviction.
  const selectTab = useCallback((tabId: string) => {
    setActiveTabId(tabId);
    const next = (activityCounter.current += 1);
    setTabs((prev) =>
      prev.map((t) => (t.id === tabId ? { ...t, lastActive: next } : t)),
    );
  }, []);

  const createNewTab = useCallback(() => {
    const newId = uuidv4();
    const newTab: ChatTab = {
      id: newId,
      name: "New chat",
      messages: [
        {
          id: uuidv4(),
          role: "assistant",
          content: "Hello! I'm Prism Ask. Ask me anything about the dataset.",
        },
      ],
      chartContext: null,
      sessionId: null,
      usage: emptyUsage(),
      lastActive: (activityCounter.current += 1),
    };
    setTabs((prev) => withCap([...prev, newTab]));
    setActiveTabId(newId);
    setInput("");
  }, []);

  const openSessionFromHistory = useCallback(
    async (summary: SessionSummary) => {
      const existing = tabs.find((t) => t.sessionId === summary.session_id);
      if (existing) {
        selectTab(existing.id);
        return;
      }

      const detail = await fetchSessionDetail(summary.session_id);
      const newId = uuidv4();
      const newTab: ChatTab = {
        id: newId,
        name: detail.title || summary.title || summary.last_message_preview || "Untitled chat",
        messages: messagesFromDetail(detail),
        chartContext: null,
        sessionId: detail.session_id,
        usage: usageFromDetail(detail),
        lastActive: (activityCounter.current += 1),
      };
      setTabs((prev) => withCap([...prev, newTab]));
      setActiveTabId(newId);
    },
    [tabs, selectTab],
  );

  const closeTab = useCallback((tabId: string) => {
    if (tabs.length === 1) return;
    const newTabs = tabs.filter((t) => t.id !== tabId);
    setTabs(newTabs);
    if (activeTabId === tabId) {
      setActiveTabId(newTabs[newTabs.length - 1].id);
    }
  }, [tabs, activeTabId]);

  const activeChart = activeTab.chartContext
    ? chartItems.find(c => c.id === activeTab.chartContext) ?? null
    : null;

  const setChartContext = useCallback((chartId: string | null) => {
    setTabs((prev) =>
      prev.map((t) =>
        t.id === activeTabId ? { ...t, chartContext: chartId } : t
      )
    );
  }, [activeTabId]);

  useEffect(() => {
    if (activeTab.chartContext && !activeChart) setChartContext(null);
  }, [activeTab.chartContext, activeChart, setChartContext]);

  const handleSend = useCallback(async () => {
    if (!input.trim() || isLoading) return;

    const isFirstUserTurn = !activeTab.messages.some((m) => m.role === "user");

    const userMessage: Message = {
      id: uuidv4(),
      role: "user",
      content: input,
    };

    setTabs((prev) =>
      prev.map((t) =>
        t.id === activeTabId
          ? { ...t, messages: [...t.messages, userMessage] }
          : t
      )
    );
    const messageToSend = input;
    setInput("");
    setIsLoading(true);

    try {
      const chartInfo: ChartInfo | null = activeChart
        ? {
            id: activeChart.id,
            name: activeChart.title,
            description: activeChart.description,
            data: activeChart.getContextData?.() ?? null,
          }
        : null;

      const response = await sendChatMessage(
        messageToSend,
        activeTab.sessionId ?? undefined,
        chartInfo,
      );

      const { usage } = response;
      const turnAgentCost = usage.cost_usd ?? 0;
      const turnRouterCost = usage.router_cost_usd ?? 0;
      const turnInputTokens = usage.input_tokens + usage.router_input_tokens;
      const turnOutputTokens = usage.output_tokens + usage.router_output_tokens;

      const assistantMessage: Message = {
        id: uuidv4(),
        role: "assistant",
        content: response.response,
        turnInputTokens,
        turnOutputTokens,
      };

      setTabs((prev) =>
        prev.map((t) => {
          if (t.id !== activeTabId) return t;
          return {
            ...t,
            sessionId: response.session_id,
            name:
              isFirstUserTurn && isDefaultTabName(t.name)
                ? messageToSend.trim().slice(0, 60)
                : t.name,
            messages: [...t.messages, assistantMessage],
            usage: {
              inputTokens: t.usage.inputTokens + turnInputTokens,
              outputTokens: t.usage.outputTokens + turnOutputTokens,
              costUsd: t.usage.costUsd + turnAgentCost + turnRouterCost,
            },
          };
        })
      );
    } catch (error) {
      console.error("Chat error:", error);
      const detail = error instanceof Error ? error.message : "";
      const refMatch = /Reference:\s*(\S+)/i.exec(detail);
      const content = refMatch
        ? `Sorry, there was an error processing your request. Reference: ${refMatch[1]}`
        : "Sorry, there was an error processing your request. Please try again.";
      const errorMessage: Message = {
        id: uuidv4(),
        role: "assistant",
        content,
      };

      setTabs((prev) =>
        prev.map((t) =>
          t.id === activeTabId
            ? { ...t, messages: [...t.messages, errorMessage] }
            : t
        )
      );
    } finally {
      setIsLoading(false);
    }
  }, [input, activeTabId, isLoading, activeTab.messages, activeTab.sessionId, activeChart]);

  const handleSuggestedQuestion = useCallback((question: string) => {
    setInput(question);
  }, []);

  const startEditMessage = useCallback((message: Message) => {
    setEditingMessageId(message.id);
    setEditingContent(message.content);
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingMessageId(null);
    setEditingContent("");
  }, []);

  const confirmEdit = useCallback(() => {
    if (!editingMessageId || !editingContent.trim()) return;

    const messageIndex = activeTab.messages.findIndex(
      (m) => m.id === editingMessageId
    );
    if (messageIndex === -1) return;

    // Branch conversation from edited point
    const newMessages = activeTab.messages.slice(0, messageIndex);
    const editedMessage: Message = {
      id: uuidv4(),
      role: "user",
      content: editingContent,
    };
    newMessages.push(editedMessage);

    setTabs((prev) =>
      prev.map((t) =>
        t.id === activeTabId ? { ...t, messages: newMessages } : t
      )
    );

    setEditingMessageId(null);
    setEditingContent("");

    // Mock response after edit
    setTimeout(() => {
      const assistantMessage: Message = {
        id: uuidv4(),
        role: "assistant",
        content: "This is a response to your edited question. The conversation has branched from this point.",
      };
      setTabs((prev) =>
        prev.map((t) =>
          t.id === activeTabId
            ? { ...t, messages: [...t.messages, assistantMessage] }
            : t
        )
      );
    }, 500);
  }, [editingMessageId, editingContent, activeTab.messages, activeTabId]);

  const hasUserMessages = activeTab.messages.some((m) => m.role === "user");

  return (
    <div className="h-full flex flex-col bg-background border border-border rounded-lg shadow-sm min-w-0 overflow-hidden">
      {/* Header with Tabs */}
      <div className="border-b border-border">
        <div className="flex items-center justify-between px-3 py-2">
          <div className="flex items-center gap-2 min-w-0">
            <Bot className="h-5 w-5 text-primary shrink-0" />
            <span className="font-semibold text-foreground text-sm truncate">Prism Ask</span>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground mr-2">
                    <Shield className="h-3 w-3 text-green-500" />
                    <span>RBAC</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Responses respect your access permissions</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={createNewTab} title="New chat">
              <Plus className="h-4 w-4" />
            </Button>
            <ChatHistoryMenu
              onSelect={openSessionFromHistory}
              openSessionIds={tabs
                .map((t) => t.sessionId)
                .filter((id): id is string => id !== null)}
            />
            {onResetSize && (
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onResetSize} title="Reset size">
                <RotateCcw className="h-4 w-4" />
              </Button>
            )}
            {onToggleMaximize && (
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={onToggleMaximize}
                title={isMaximized ? "Restore" : "Maximize"}
              >
                {isMaximized ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </Button>
            )}
            {onClose && (
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose} title="Close">
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Tab Bar */}
        <div className="px-2 pb-2">
          <Tabs value={activeTabId} onValueChange={selectTab}>
            <TabsList className="h-8 w-full justify-start gap-1 bg-muted/50 p-1">
              {tabs.map((tab) => (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id}
                  className="relative flex-1 min-w-[90px] max-w-[180px] text-xs px-3 py-1 border border-border rounded-md bg-background/40 data-[state=active]:bg-background data-[state=active]:border-primary/40 data-[state=active]:shadow-sm"
                >
                  <span className="min-w-0 flex-1 truncate text-left" title={tab.name}>
                    {tab.name}
                  </span>
                  {tabs.length > 1 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        closeTab(tab.id);
                      }}
                      className="ml-2 flex-shrink-0 hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      </div>


      {/* Session cost */}
      <div className="px-4 py-2 border-b">
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Session cost</span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="font-medium tabular-nums cursor-default">
                  {costFormatter.format(activeTab.usage.costUsd)}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">
                  {activeTab.usage.inputTokens.toLocaleString()} in · {activeTab.usage.outputTokens.toLocaleString()} out
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>


      {/* Messages */}
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
        {isBootstrapping ? (
          <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading your chats…
          </div>
        ) : (
        <div className="space-y-4 p-4">
          {activeTab.messages.map((message, messageIndex) => (
            <div key={message.id}>
              <div
                className={`flex gap-3 ${message.role === "user" ? "justify-end" : "justify-start"
                  }`}
              >
                {message.role === "assistant" && (
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Bot className="h-4 w-4 text-primary" />
                  </div>
                )}
                <div className="flex flex-col min-w-0 overflow-hidden" style={{ maxWidth: 'calc(100% - 44px)' }}>
                  {editingMessageId === message.id ? (
                    <div className="flex flex-col gap-2">
                      <Input
                        value={editingContent}
                        onChange={(e) => setEditingContent(e.target.value)}
                        className="text-sm"
                        autoFocus
                      />
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={confirmEdit}>
                          <Check className="h-3 w-3 mr-1" />
                          Rerun
                        </Button>
                        <Button size="sm" variant="ghost" onClick={cancelEdit}>
                          <XCircle className="h-3 w-3 mr-1" />
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div
                        className={`rounded-lg px-4 py-2 ${message.role === "user"
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted text-foreground"
                          }`}
                        style={{ maxWidth: '100%' }}
                      >
                        <div className="text-sm prose prose-sm dark:prose-invert [&>*]:max-w-full">
                          <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                            {message.content}
                          </Markdown>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        {message.role === "user" && (
                          <button
                            onClick={() => startEditMessage(message)}
                            className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                          >
                            <Pencil className="h-3 w-3" />
                            Edit & Rerun
                          </button>
                        )}
                        {message.cached && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <Badge variant="secondary" className="text-xs h-5">
                                  <Zap className="h-3 w-3 mr-1" />
                                  Cached
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Response from semantic cache</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        {message.role === "assistant" && message.turnInputTokens !== undefined && message.turnOutputTokens !== undefined && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="text-xs text-muted-foreground tabular-nums cursor-default">
                                  {message.turnInputTokens.toLocaleString()} in · {message.turnOutputTokens.toLocaleString()} out
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="text-xs">agent + router for this turn (incl. tool + intent)</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                    </>
                  )}
                </div>
                {message.role === "user" && editingMessageId !== message.id && (
                  <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <User className="h-4 w-4 text-accent" />
                  </div>
                )}
              </div>

              {/* Suggested questions after first assistant message */}
              {messageIndex === 0 && message.role === "assistant" && !hasUserMessages && (
                <div className="flex flex-wrap gap-2 ml-11 mt-4">
                  {SUGGESTED_QUESTIONS.map((question, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSuggestedQuestion(question)}
                      className="text-sm bg-primary/5 border border-primary/20 rounded-lg px-4 py-2 hover:bg-primary/10 hover:border-primary/40 transition-all text-left text-foreground cursor-pointer shadow-sm hover:shadow"
                    >
                      {question}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
          
          {/* Loading spinner */}
          {isLoading && (
            <div className="flex gap-3 justify-start">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Bot className="h-4 w-4 text-primary" />
              </div>
              <div className="flex items-center gap-2 bg-muted text-foreground rounded-lg px-4 py-2">
                <Loader2 className="h-4 w-4 animate-spin text-primary" />
                <span className="text-sm text-muted-foreground">Thinking...</span>
              </div>
            </div>
          )}
          
          {/* Auto-scroll anchor */}
          <div ref={messagesEndRef} />
        </div>
        )}
      </div>

      {/* Input with Cursor-style context selector */}
      <div className="p-4 border-t">
        {/* Chart context indicator */}
        {activeChart && (
          <div className="flex items-center gap-2 mb-2 px-1">
            <Badge variant="secondary" className="text-xs flex items-center gap-1.5 bg-primary/10 text-primary border-primary/30">
              <BarChart3 className="h-3 w-3" />
              Asking about: {activeChart.title}
              <button 
                onClick={() => setChartContext(null)}
                className="ml-1 hover:bg-primary/20 rounded-full p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          </div>
        )}
        <div className="flex flex-col gap-2 rounded-lg border border-border bg-background p-2">
          <Textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            rows={1}
            wrap="soft"
            placeholder={activeChart ? `Ask about ${activeChart.title}...` : "Type a question about your data..."}
            className="flex-1 resize-none border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 px-1 py-1.5 min-h-0 overflow-x-hidden overflow-y-auto whitespace-pre-wrap break-words"
          />

          {/* Context controls row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              {/* Chart context dropdown - shows only charts visible to user */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant={activeTab.chartContext ? "secondary" : "ghost"} 
                    size="sm" 
                    className={`h-7 px-2 text-xs gap-1 transition-colors ${activeTab.chartContext ? "text-foreground bg-primary/10 border border-primary/30 hover:bg-primary/20" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}
                  >
                    {activeTab.chartContext ? (
                      <BarChart3 className="h-3.5 w-3.5" />
                    ) : (
                      <MessageSquare className="h-3.5 w-3.5" />
                    )}
                    {activeTab.chartContext ? (
                      activeChart?.title
                    ) : (
                      "General Questions"
                    )}
                    <ChevronDown className="h-3 w-3 opacity-50" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-80 max-h-[60vh] overflow-y-auto">
                  <DropdownMenuItem
                    onClick={() => setChartContext(null)}
                    className="flex items-center gap-2 py-2"
                  >
                    <MessageSquare className="h-4 w-4 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="font-medium">General Questions</span>
                      <p className="text-xs text-muted-foreground truncate">Ask anything about the platform</p>
                    </div>
                    {!activeTab.chartContext && <Check className="h-4 w-4 ml-auto flex-shrink-0 text-primary" />}
                  </DropdownMenuItem>
                  {chartItems.map((chart) => (
                    <DropdownMenuItem
                      key={chart.id}
                      onClick={() => setChartContext(chart.id)}
                      className="flex items-center gap-2 py-2"
                    >
                      <BarChart3 className="h-4 w-4 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <span className="font-medium">{chart.title}</span>
                        <p className="text-xs text-muted-foreground line-clamp-1">{chart.description}</p>
                      </div>
                      {activeTab.chartContext === chart.id && (
                        <Check className="h-4 w-4 ml-auto flex-shrink-0 text-primary" />
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <Button
              onClick={handleSend}
              size="icon"
              className="h-7 w-7"
              disabled={!input.trim() || isLoading}
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ChatInterface;
